import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import PlaygroundWidget from "../../components/mcp-platform/playground-widget/PlaygroundWidget";
import backend from "../../backend/backend";

const McpshowcaseWidgetPage = () => {
  const { playgroundId } = useParams();
  const [searchParams] = useSearchParams();
  const [playground, setPlayground] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uiMode, setUiMode] = useState("LIGHT");
  const containerRef = useRef(null);
  const resizeObserverRef = useRef(null);

  // Function to send height update to parent window
  const sendHeightUpdate = useCallback((height) => {
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(
          {
            type: "mcpshowcase-height-update",
            height: height,
          },
          "*"
        ); // Using "*" for cross-origin communication
      } catch (error) {
        console.error("MCPShowcase: Error sending height update:", error);
      }
    }
  }, []);

  // Function to send ready message to parent window
  const sendReadyMessage = useCallback(() => {
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(
          {
            type: "mcpshowcase-ready",
          },
          "*"
        );
      } catch (error) {
        console.error("MCPShowcase: Error sending ready message:", error);
      }
    }
  }, []);

  // Function to send error message to parent window
  const sendErrorMessage = useCallback((message) => {
    if (window.parent && window.parent !== window) {
      try {
        window.parent.postMessage(
          {
            type: "mcpshowcase-error",
            message: message,
          },
          "*"
        );
      } catch (error) {
        console.error("MCPShowcase: Error sending error message:", error);
      }
    }
  }, []);

  // Set up ResizeObserver to watch for content height changes
  useEffect(() => {
    if (!containerRef.current) return;

    const updateHeight = () => {
      if (containerRef.current) {
        const height = containerRef.current.scrollHeight;
        sendHeightUpdate(height);
      }
    };

    // Create ResizeObserver to watch for content changes
    if (window.ResizeObserver) {
      resizeObserverRef.current = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const height = entry.contentRect.height;
          sendHeightUpdate(height);
        }
      });

      resizeObserverRef.current.observe(containerRef.current);
    } else {
      // Fallback for browsers that don't support ResizeObserver
      const interval = setInterval(updateHeight, 100);
      return () => clearInterval(interval);
    }

    // Initial height update
    updateHeight();

    return () => {
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
      }
    };
  }, [sendHeightUpdate]);

  useEffect(() => {
    const loadPlayground = async () => {
      try {
        // Get widget parameters from URL
        const widgetParams = searchParams.get("widget_params");
        if (!widgetParams) {
          const errorMsg = "No widget parameters provided";
          setError(errorMsg);
          sendErrorMessage(errorMsg);
          setLoading(false);
          return;
        }

        // Decode parameters
        const params = JSON.parse(decodeURIComponent(atob(widgetParams)));
        console.log("MCPShowcase Widget: Received params:", params);

        if (params.uiMode) {
          setUiMode(params.uiMode);
          console.log("MCPShowcase Widget: UI mode:", params.uiMode);
          // Set the body background color based on uiMode
          if (params.uiMode === "DARK") {
            document.body.style.backgroundColor = "#141414";
            console.log(
              "MCPShowcase Widget: Setting body background color to dark ",
              document.body.style.backgroundColor
            );
          } else {
            document.body.style.backgroundColor = "#f8f9fa";
            console.log(
              "MCPShowcase Widget: Setting body background color to light",
              document.body.style.backgroundColor
            );
          }
        }

        setLoading(true);
        // Fetch playground data
        backend
          .getPublicPlayground(playgroundId)
          .then((playgroundData) => {
            console.log(
              "MCPShowcase Widget: Playground loaded:",
              playgroundData
            );
            setPlayground(playgroundData);
            setLoading(false);
            // Record a widget view (fire-and-forget, raw count).
            try {
              const clientId =
                params.clientId ||
                (window.crypto && window.crypto.randomUUID
                  ? window.crypto.randomUUID()
                  : "c" + Date.now());
              backend.recordWidgetView(playgroundId, clientId);
            } catch (e) {
              /* non-fatal */
            }
            // Send ready message after successful load
            setTimeout(sendReadyMessage, 100);
          })
          .catch((err) => {
            const errorMsg = `Failed to load playground: ${err.message}`;
            setError(errorMsg);
            sendErrorMessage(errorMsg);
            setLoading(false);
          });
      } catch (err) {
        console.error("MCPShowcase Widget: Error loading playground:", err);
        const errorMsg = err.message;
        setError(errorMsg);
        sendErrorMessage(errorMsg);
        setLoading(false);
      }
    };

    loadPlayground();
  }, [searchParams, sendReadyMessage, sendErrorMessage]);

  // Update height when loading state changes
  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        if (containerRef.current) {
          const height = containerRef.current.scrollHeight;
          sendHeightUpdate(height);
        }
      }, 100);
    }
  }, [loading, sendHeightUpdate]);

  // Update height when error state changes
  useEffect(() => {
    if (error) {
      setTimeout(() => {
        if (containerRef.current) {
          const height = containerRef.current.scrollHeight;
          sendHeightUpdate(height);
        }
      }, 100);
    }
  }, [error, sendHeightUpdate]);

  if (loading) {
    return (
      <div
        ref={containerRef}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          backgroundColor: uiMode === "DARK" ? "#141414" : "#fff",
          color: uiMode === "DARK" ? "#fff" : "#000",
        }}
      >
        <div>Loading MCP Playground...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        ref={containerRef}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          backgroundColor: uiMode === "DARK" ? "#141414" : "#fff",
          color: uiMode === "DARK" ? "#fff" : "#000",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <div>
          <h3>Error Loading Playground</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!playground) {
    return (
      <div
        ref={containerRef}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          backgroundColor: uiMode === "DARK" ? "#141414" : "#fff",
          color: uiMode === "DARK" ? "#fff" : "#000",
        }}
      >
        <div>Playground not found</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: "100%",
        minHeight: "400px",
      }}
    >
      <PlaygroundWidget playground={playground} uiMode={uiMode} />
    </div>
  );
};

export default McpshowcaseWidgetPage;
